import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowDropDown } from '@mui/icons-material';
import { Pagination, Tooltip, CircularProgress } from '@mui/material';
import bageUrl from 'assets/images/ranking/bage.svg';
import firstUrl from 'assets/images/ranking/one.svg';
import secondUrl from 'assets/images/ranking/two.svg';
import thirdUrl from 'assets/images/ranking/three.svg';
import { Season1RankingsQuery, Season2RankingsQuery } from 'gql-ops/graph/__generated__/request';
import { getOnePieceServerApi } from 'gql-ops';
import { formatStatsValue } from 'common-helpers/utils';
import { CardStyles, GradientTextColor } from 'theme/commonStyles';
import { RowBetween } from 'components/Row';
import { TYPE } from 'theme';

const RankingWrapper = styled.div`
    padding: 124px 0 84px;
    min-height: calc(100vh - 362px);
    ${({ theme }) => theme.mediaWidth.upToSmall`
        min-height: calc(100vh - 453px);
    `};
`;

const SeasonButton = styled.div<{ selected?: boolean }>`
    padding: 10px 30px;
    border: 1px solid #fff;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    background: ${({ selected, theme }) => (selected ? theme.c.linearGradientBg : 'transparent')};
    color: ${({ selected }) => (selected ? '#fff' : '#fff')};
    opacity: ${({ selected }) => (selected ? 1 : 0.7)};
    cursor: pointer;
    transition: background 0.2s, opacity 0.2s;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 16px;
        padding: 8px 20px;
    `};
}`;

const Card = styled.div`
    ${CardStyles}
    margin: 0 auto;
    padding: 45px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    border-radius: 8px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        max-width: calc(100vw - 32px);
        padding: 20px;
    `};
`;

const TitleWrapper = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        gap: 5.5px;
    `};
`;

const Bage = styled.img`
    width: 44px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 16px;
    `};
`;

const Title = styled.div`
    ${GradientTextColor}
    display: inline-block;
    font-size: 24px;
    font-weight: 700;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 16px;
    `};
`;

const SubTitle = styled.div`
    font-size: 14px;
    font-weight: 400;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 12px;
    `};
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    `};
`;

const Row = styled.div`
    display: flex;
    border-radius: 20px;

    &.ranking-1 {
        background: #0f0e1b;
    }

    &.ranking-2 {
        background: rgba(15, 14, 27, 0.5);
    }

    &.ranking-3 {
        background: rgba(15, 14, 27, 0.3);
    }

    &.skeleton {
        animation: 2s ease-in-out infinite skeleton-pulsate;
    }

    &:not(.no-hover):hover {
        background: linear-gradient(
            90deg,
            rgba(106, 13, 173, 0.5) 0%,
            rgba(247, 147, 26, 0.12) 55%,
            rgba(247, 147, 26, 0) 100%
        );
    }
`;

const Cell = styled.div<{ isBold?: boolean; cursor?: any }>`
    flex: 1;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ cursor }) => cursor || 'default'};
    font-weight: ${({ isBold }) => (isBold ? 700 : 400)};
    font-size: 18px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 48px;
        font-size: 12px;

        &.ranking-cell {
            flex: 0 0 46px;
        }
    `};
`;

const StyledPagination = styled(Pagination)`
    display: flex;
    justify-content: center;
    color: #fff;

    button {
        color: #fff;
        min-width: 20px;
        width: 20px;
        height: 20px;
        font-size: 12px;
    }

    button.Mui-selected:hover,
    button:hover {
        opacity: 0.8;
    }

    button.Mui-selected {
        background: ${({ theme }) => theme.c.linearGradientBg};
    }
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    gap: 16px;
    color: #888;
`;

function formatGoatPoints(value: string, decimals = 2): string {
    return formatStatsValue(value, decimals);
}

export default observer(function Ranking() {
    const [page, setPage] = useState(1);
    const [season, setSeason] = useState<1 | 2>(2);
    const [season1Rankings, setSeason1Rankings] = useState<Season1RankingsQuery['season1Rankings']>([]);
    const [season2Rankings, setSeason2Rankings] = useState<Season2RankingsQuery['season2Rankings']>([]);
    const PAGE_SIZE = 20;

    // 排序状态：'desc' | 'asc'
    const [goatSort, setGoatSort] = useState<'desc' | 'asc'>('desc');

    // activeRankings: 当前season榜单
    const activeRankings = useMemo(() => {
        return season === 1 ? season1Rankings : season2Rankings;
    }, [season, season1Rankings, season2Rankings]);
    const pageRankings = useMemo(() => {
        return activeRankings.slice((Number(page) - 1) * PAGE_SIZE, Number(page) * PAGE_SIZE);
    }, [activeRankings, page]);
    const paginationCount = Math.ceil(activeRankings.length / PAGE_SIZE);

    const [loading, setLoading] = useState(false);

    // 修改refresh，支持只请求某个season榜单
    const refresh = useCallback(
        async (seasonNum: 1 | 2) => {
            setLoading(true);
            const sdk = getOnePieceServerApi();
            try {
                if (seasonNum === 1) {
                    const season1Result = await sdk.season1Rankings();
                    setSeason1Rankings(season1Result?.season1Rankings ?? []);
                } else {
                    const season2Result = await sdk.season2Rankings();
                    setSeason2Rankings(season2Result?.season2Rankings ?? []);
                }
            } finally {
                setLoading(false);
            }
        },
        [setSeason1Rankings, setSeason2Rankings],
    );

    useEffect(() => {
        refresh(2);
    }, [refresh]);

    // 切换season时清空榜单并主动请求新数据
    const handleSeasonChange = async (seasonNum: 1 | 2) => {
        setPage(1);
        setSeason(seasonNum);
        if (seasonNum === 1) {
            setSeason1Rankings([]);
        } else {
            setSeason2Rankings([]);
        }
        await refresh(seasonNum);
    };

    // 排序处理函数
    const handleGoatSort = () => {
        const newSort = goatSort === 'desc' ? 'asc' : 'desc';
        setGoatSort(newSort);
        setPage(1);
        if (season === 1) {
            setSeason1Rankings((prev) => {
                const sorted = [...prev].sort((a, b) => {
                    const aVal = Number(a.totalPoint);
                    const bVal = Number(b.totalPoint);
                    // 首先按totalPoint排序
                    if (aVal !== bVal) {
                        return newSort === 'desc' ? bVal - aVal : aVal - bVal;
                    }
                    // 如果totalPoint相同，按rank排序
                    return newSort === 'desc' ? a.rank - b.rank : b.rank - a.rank;
                });
                return sorted;
            });
        } else {
            setSeason2Rankings((prev) => {
                const sorted = [...prev].sort((a, b) => {
                    const aVal = Number(a.totalPoint);
                    const bVal = Number(b.totalPoint);
                    // 首先按totalPoint排序
                    if (aVal !== bVal) {
                        return newSort === 'desc' ? bVal - aVal : aVal - bVal;
                    }
                    // 如果totalPoint相同，按rank排序
                    return newSort === 'desc' ? a.rank - b.rank : b.rank - a.rank;
                });
                return sorted;
            });
        }
    };
    return (
        <RankingWrapper>
            <Card>
                <RowBetween>
                    <TitleWrapper>
                        <Bage src={bageUrl} />
                        <div>
                            <Title>Total User Ranking</Title>
                            <SubTitle>
                                View the rankings of all users&apos; GOAT Points and invitations members.
                            </SubTitle>
                        </div>
                    </TitleWrapper>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <SeasonButton selected={season === 1} onClick={() => handleSeasonChange(1)}>
                            Season 1
                        </SeasonButton>
                        <SeasonButton selected={season === 2} onClick={() => handleSeasonChange(2)}>
                            Season 2
                        </SeasonButton>
                    </div>
                </RowBetween>

                <Table>
                    <Row className="no-hover">
                        <Cell className="ranking-cell">Ranking</Cell>
                        <Cell>Address</Cell>
                        <Cell style={{ cursor: 'pointer' }} onClick={handleGoatSort}>
                            Total GOAT Points
                            {goatSort === 'desc' ? (
                                <ArrowDropDown />
                            ) : (
                                <ArrowDropDown style={{ transform: 'rotate(180deg)' }} />
                            )}
                        </Cell>
                        {season === 2 && <Cell>Invitation Members</Cell>}
                    </Row>
                    {loading ? (
                        <LoadingWrapper>
                            <CircularProgress />
                        </LoadingWrapper>
                    ) : (
                        <>
                            {pageRankings.length > 0 &&
                                pageRankings.map((row) => {
                                    const { rank, address, totalPoint } = row;
                                    const rankImgUrl = [firstUrl, secondUrl, thirdUrl][rank - 1];
                                    // season1没有inviteCount，season2是inviteeCount
                                    const inviteCount = season === 2 ? (row as any).inviteeCount : '-';
                                    return (
                                        <Row key={`${rank}-${address}`} className={`ranking-${rank}`}>
                                            <Cell className="ranking-cell">
                                                {rankImgUrl ? <img src={rankImgUrl} alt={`Ranking ${rank}`} /> : rank}
                                            </Cell>
                                            <Cell>
                                                <Tooltip title={address} placement="top">
                                                    <span>
                                                        {address.slice(0, 6)}
                                                        ...{address.slice(-4)}
                                                    </span>
                                                </Tooltip>
                                            </Cell>
                                            <Cell>{formatGoatPoints(totalPoint)}</Cell>
                                            {season === 2 && <Cell>{inviteCount}</Cell>}
                                        </Row>
                                    );
                                })}
                            {pageRankings.length === 0 && (
                                <EmptyWrapper>
                                    <TYPE.desc fontSize={16}>No Ranking Data Found</TYPE.desc>
                                    <TYPE.desc fontSize={14} color="#666">
                                        There is currently no ranking data to display.
                                    </TYPE.desc>
                                </EmptyWrapper>
                            )}
                        </>
                    )}
                </Table>
                {paginationCount > 1 && (
                    <StyledPagination
                        count={paginationCount}
                        page={Number(page)}
                        onChange={(event, nextPage) => setPage(Number(nextPage))}
                    />
                )}
            </Card>
        </RankingWrapper>
    );
});
